// Batch 28 · Follow-up 1 · F2 (b) safeguard 5 — before the session ends with unsaved changes on screen, a warning offers to Save
// now. The session ends at the sign-in token's expiry time (``exp`` in the token the app already holds — read here, never
// changed); nothing about sign-in, 2FA or the token is touched. Shown only while a Save / Cancel bar has unsaved changes.
import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { hasUnsaved, saveAllUnsaved, subscribeUnsaved } from "@/lib/unsavedChanges";

export const SEW_TEXT = {
  en: { msg: "Your session ends in {m} min and this page has unsaved changes.", ended: "Your session has ended — unsaved changes could not be kept.",
        save: "Save now", saving: "Saving…" },
  ar: { msg: "تنتهي جلستك خلال {m} دقيقة وفي هذه الصفحة تغييرات غير محفوظة.", ended: "انتهت جلستك — تعذّر الاحتفاظ بالتغييرات غير المحفوظة.",
        save: "احفظ الآن", saving: "جارٍ الحفظ…" },
};
export const WARN_BEFORE_MS = 10 * 60 * 1000;   // ten minutes before the end

/** The token's expiry (ms since epoch), or null when there is none / it cannot be read. */
export function tokenExpiryMs(token) {
  try {
    const part = String(token || "").split(".")[1];
    if (!part) return null;
    const json = JSON.parse(atob(part.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(part.length / 4) * 4, "=")));
    return typeof json.exp === "number" ? json.exp * 1000 : null;
  } catch (e) { return null; }
}

function readToken() { try { return localStorage.getItem("af_token"); } catch (e) { return null; } }

export default function SessionExpiryWarning({ lang = "en", now = () => Date.now(), tickMs = 30000 }) {
  const L = SEW_TEXT[lang === "ar" ? "ar" : "en"];
  const [, setTick] = useState(0);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    const iv = setInterval(() => setTick((x) => x + 1), tickMs);
    const off = subscribeUnsaved(() => setTick((x) => x + 1));
    return () => { clearInterval(iv); off(); };
  }, [tickMs]);
  const exp = tokenExpiryMs(readToken());
  if (!exp || !hasUnsaved()) return null;
  const left = exp - now();
  if (left > WARN_BEFORE_MS) return null;
  const mins = Math.max(0, Math.ceil(left / 60000));
  return (
    <div className="fixed bottom-4 start-4 z-50 flex max-w-md flex-wrap items-center gap-2 rounded border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-900 shadow dark:bg-red-950/60 dark:text-red-100"
         role="alert" data-testid="session-expiry-warning">
      <span data-testid="session-expiry-warning-text">{left > 0 ? L.msg.replace("{m}", String(mins)) : L.ended}</span>
      {left > 0 && (
        <Button size="sm" disabled={saving} data-testid="session-expiry-save-now"
                onClick={async () => { setSaving(true); try { await saveAllUnsaved(); } finally { setSaving(false); } }}>
          {saving ? L.saving : L.save}</Button>
      )}
    </div>
  );
}
